'use client';

import type { PlanProgressPayload, PlanProgressStep, PlanPromptItem } from '@/@studio-ai/types';
import { humanizeLabel } from '@/@studio-ai/utils/text-utils';
import { Chip, Divider } from '@heroui/react';
import { Icon } from '@iconify/react';
import { tv } from 'tailwind-variants';

const row = tv({
    slots: {
        base: 'flex flex-col gap-1 py-3',
        label: 'text-[12px] font-semibold  text-default-400',
        answer: 'text-[14px] leading-snug text-foreground',
    },
    variants: {
        status: {
            pending: {
                base: 'opacity-60',
                answer: 'italic text-default-400',
            },
            answered: {},
            skipped: {
                answer: 'italic text-default-400',
            },
        },
    },
    defaultVariants: {
        status: 'pending',
    },
});

function answerDisplay(step: PlanProgressStep, humanize: boolean): string {
    if (step.status === 'skipped') return 'Skipped';
    if (step.status === 'pending') return '—';
    const raw = step.answer;
    if (raw === undefined || raw === null) return '—';
    const trimmed = String(raw).trim();
    if (trimmed.length === 0) return '—';
    if (!humanize) return trimmed;
    const parts = trimmed
        .split(',')
        .map((piece) => humanizeLabel(piece.trim()))
        .filter((piece) => piece.length > 0);
    return parts.length > 0 ? parts.join(', ') : trimmed;
}

function promptTitle(prompt: PlanPromptItem, index: number): string {
    const section = prompt.section_name?.trim();
    if (section) return humanizeLabel(section);
    if (prompt.type) return humanizeLabel(prompt.type);
    return `Prompt ${index + 1}`;
}

export function PlanSidebar({
    payload,
    humanizeAnswers = true,
}: {
    payload: PlanProgressPayload;
    /** Answers are planner codes (`audience_profiling`) by default and are
     * turned into words. Pass false when they are already what the user
     * picked (a survey or brand name), which humanizing would garble. */
    humanizeAnswers?: boolean;
}) {
    const steps = payload.steps ?? [];
    const answeredCount = steps.filter((s) => s.status === 'answered').length;
    const totalSteps = payload.totalSteps ?? steps.length;
    const prompts: PlanPromptItem[] = (payload.prompts ?? [])
        .slice()
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    return (
        <aside className="flex h-full w-80 shrink-0 flex-col border-l border-divider bg-content1">
            <div className="flex items-center justify-between gap-2 px-5 py-4">
                <div className="flex flex-col">
                    <span className="text-tiny font-semibold uppercase tracking-[0.12em] text-default-500">
                        Your plan
                    </span>
                    {payload.templateName && (
                        <span className="mt-0.5 text-small font-semibold text-foreground">
                            {payload.templateName}
                        </span>
                    )}
                    {payload.brand && (
                        <span className="text-tiny text-default-400">{payload.brand}</span>
                    )}
                </div>
                <Chip
                    size="sm"
                    variant="flat"
                    color="default"
                    classNames={{
                        base: 'h-6 flex-shrink-0',
                        content: 'text-tiny font-medium px-1.5 tabular-nums',
                    }}
                >
                    {answeredCount} / {totalSteps}
                </Chip>
            </div>

            <Divider />

            <div className="flex-1 overflow-y-auto px-5 py-2">
                {steps.length === 0 && prompts.length === 0 ? (
                    <div className="flex h-full items-center justify-center py-10 text-tiny text-default-400">
                        No steps yet
                    </div>
                ) : (
                    <>
                        {steps.length > 0 && (
                            <ul>
                                {steps.map((step, idx) => {
                                    const styles = row({ status: step.status });
                                    const iconName =
                                        step.status === 'answered'
                                            ? 'lucide:check-circle-2'
                                            : step.status === 'skipped'
                                              ? 'lucide:minus-circle'
                                              : 'lucide:circle';
                                    const iconColor =
                                        step.status === 'answered'
                                            ? 'text-success-500'
                                            : step.status === 'skipped'
                                              ? 'text-default-400'
                                              : 'text-default-300';
                                    return (
                                        <li key={`${step.id}-${idx}`} className={styles.base()}>
                                            <div className="flex items-center gap-2">
                                                <Icon
                                                    icon={iconName}
                                                    className={`h-3.5 w-3.5 ${iconColor}`}
                                                />
                                                <span className={styles.label()}>
                                                    {humanizeLabel(step.label)}
                                                </span>
                                                {step.adHoc && (
                                                    <Chip
                                                        size="sm"
                                                        variant="flat"
                                                        color="secondary"
                                                        classNames={{
                                                            base: 'h-4 ml-auto',
                                                            content: 'text-mini font-medium px-1',
                                                        }}
                                                    >
                                                        custom
                                                    </Chip>
                                                )}
                                            </div>
                                            <span className={styles.answer()}>
                                                {answerDisplay(step, humanizeAnswers)}
                                            </span>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                        {prompts.length > 0 && (
                            <>
                                {steps.length > 0 && <Divider className="my-3" />}
                                <div className="flex items-center justify-between gap-2 pb-2">
                                    <span className="text-tiny font-semibold uppercase tracking-[0.12em] text-default-500">
                                        Prompts
                                    </span>
                                    <Chip
                                        size="sm"
                                        variant="flat"
                                        color="default"
                                        classNames={{
                                            base: 'h-5 flex-shrink-0',
                                            content: 'text-mini font-medium px-1.5 tabular-nums',
                                        }}
                                    >
                                        {prompts.length}
                                    </Chip>
                                </div>
                                <p className="pb-2 text-mini italic text-default-400">
                                    Type in the chat to add, remove, or adjust any prompt.
                                </p>
                                <ul className="flex flex-col gap-2">
                                    {prompts.map((p, index) => {
                                        const title = promptTitle(p, index);
                                        const isCustom = (p.type ?? '').toLowerCase() === 'custom';
                                        return (
                                            <li
                                                key={`${p.order}-${p.type || 'prompt'}-${index}`}
                                                className="rounded-lg border border-default-200/60 bg-content2/40 px-2.5 py-2"
                                            >
                                                <div className="flex items-center gap-2 pb-1">
                                                    <span className="text-mini font-semibold tabular-nums text-default-400">
                                                        #{p.order ?? index + 1}
                                                    </span>
                                                    <span className="truncate text-tiny font-semibold text-foreground">
                                                        {title}
                                                    </span>
                                                    {isCustom && (
                                                        <Chip
                                                            size="sm"
                                                            variant="flat"
                                                            color="secondary"
                                                            classNames={{
                                                                base: 'h-4 ml-auto',
                                                                content:
                                                                    'text-mini font-medium px-1',
                                                            }}
                                                        >
                                                            custom
                                                        </Chip>
                                                    )}
                                                </div>
                                                <p className="whitespace-pre-wrap text-mini leading-snug text-default-600">
                                                    {p.text}
                                                </p>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </>
                        )}
                    </>
                )}
            </div>
        </aside>
    );
}
